// Shared Text-to-Speech playback for chat. ONE audio element is live at a time, so
// a new utterance (a Play-voice click OR an auto-spoken reply) stops the previous —
// barge-in groundwork. Nothing here plays on its own; callers decide when (a click,
// or the Speak-replies toggle). Synthesis goes through POST /api/tts (catalog-driven
// OpenAI TTS), which stores the audio and returns a short /api/tts-audio URL.

import { api, getToken } from '@/api/client';

let current: HTMLAudioElement | null = null;
// Every audio element the voice system creates is tracked here so a teardown can pause
// ALL of them — the guaranteed "no sound after the overlay closes" backstop, immune to any
// queue/prefetch race.
const liveAudios = new Set<HTMLAudioElement>();

/** Hard-stop EVERY voice audio element (playing, buffering, or about to start). Call on
 *  voice-mode teardown so nothing keeps talking in the background after close.
 *  pause() alone leaves the media-decode pipeline warm (a faint "processing" hiss on some
 *  systems), so we also detach the source + load() to fully release each element. */
export function stopAllAudio(): void {
  for (const a of liveAudios) {
    try {
      a.pause();
      a.currentTime = 0;
      a.srcObject = null;
      a.removeAttribute('src');
      a.load(); // releases the decoder — kills the residual background processing sound
    } catch { /* noop */ }
  }
  liveAudios.clear();
  current = null;
}

/** Best MediaRecorder mime this browser supports (opus preferred), or '' for default. */
export function pickAudioMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported?.('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported?.('audio/mp4')) return 'audio/mp4';
  return '';
}

/**
 * Transcribe a recorded audio blob via POST /api/transcribe (catalog-driven STT).
 * Uses a BARE fetch (the api client forces Content-Type: application/json, which would
 * corrupt the audio body) with the bearer added manually. Returns the trimmed text, or
 * '' on silence / failure — callers decide what to do with an empty result. Shared by the
 * composer's dictation and the hands-free voice loop so the STT call lives in one place.
 */
export interface TranscribeResult { text: string; status: number }

export async function transcribeBlob(blob: Blob, signal?: AbortSignal, opts?: { fast?: boolean }): Promise<TranscribeResult> {
  const lang = (navigator.language || 'en').slice(0, 2);
  const token = getToken();
  // `fast` (the hands-free live loop) → server prefers the low-latency Groq-first STT chain +
  // a short timeout. Dictation omits it and keeps the accuracy-first path.
  const qs = `lang=${encodeURIComponent(lang)}${opts?.fast ? '&fast=1' : ''}`;
  const res = await fetch(`/api/transcribe?${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'audio/webm', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: blob,
    ...(signal ? { signal } : {}),
  });
  const data = (await res.json().catch(() => ({}))) as { text?: unknown };
  return { text: typeof data.text === 'string' ? data.text.trim() : '', status: res.status };
}

/** Stop whatever is currently speaking (if anything). */
export function stopSpeaking(): void {
  if (current) {
    try { current.pause(); current.currentTime = 0; } catch { /* noop */ }
    current = null;
  }
}

/** True while a reply is being spoken. */
export function isSpeaking(): boolean {
  return current !== null;
}

/**
 * Synthesise `text` and play it. Resolves with the Audio element once playback
 * STARTS; rejects if synthesis or playback fails. `onEnded` fires when it finishes
 * (or errors), so a caller can reset its button state.
 */
export async function speakText(
  text: string,
  onEnded?: () => void,
  shouldPlay?: () => boolean,
  instructions?: string,
): Promise<HTMLAudioElement | null> {
  const { audioUrl } = await api.post<{ audioUrl: string }>('/api/tts', { text, ...(instructions ? { instructions } : {}) });
  // The caller's session may have ended (voice mode closed / re-opened) WHILE /api/tts
  // was in flight. Registering `current` only happens below, so a teardown that ran during
  // the request could not have stopped this playback — bail before it starts, or the reply
  // would speak over a closed overlay or a fresh session.
  if (shouldPlay && !shouldPlay()) { onEnded?.(); return null; }
  stopSpeaking();
  const audio = new Audio(audioUrl);
  current = audio;
  liveAudios.add(audio);
  const done = () => { liveAudios.delete(audio); if (current === audio) current = null; onEnded?.(); };
  audio.onended = done;
  audio.onerror = done;
  await audio.play();
  return audio;
}

/** Strip markdown so a spoken chunk reads cleanly (mirrors the server ttsClean, plus
 *  fence/backtick removal since streamed chunks can split a code block). */
export function cleanForSpeech(text: string): string {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/```+/g, ' ')
    .replace(/`+/g, '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/[*_>~|#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Delivery steer for the hands-free voice mode — makes gpt-4o-mini-tts sound like a warm,
 *  relaxed human instead of a flat reader (natural pacing, intonation, the occasional soft
 *  sigh / small laugh / thoughtful "hmm" where it fits). Passed as `instructions` on /api/tts.
 *  This is the BASE/default; buildDeliveryInstructions() layers a per-turn affect on top. */
export const VOICE_MODE_INSTRUCTIONS =
  'Speak like a warm, friendly person in a real, relaxed conversation. Use natural pacing and '
  + 'genuine intonation — never flat or robotic. Let a little humanity through where it fits the '
  + 'moment: a soft sigh, a small chuckle, a thoughtful "hmm", a breath before an important point. '
  + 'Keep it easy, expressive, and human.';

/** Build a per-turn delivery steer for gpt-4o-mini-tts. Starts from the warm base, then adds one
 *  light affect clause inferred from the reply's own content (and, secondarily, the user's words),
 *  so an apology sounds gentle, good news sounds bright, and a reassuring reply stays calm —
 *  instead of every sentence getting the identical instruction. Cheap + text-only (no extra model
 *  call, no added latency); true audio-based vibe-matching is a later add. Meant to be computed
 *  ONCE per turn and reused for every sentence so the affect stays consistent across the reply. */
export function buildDeliveryInstructions(reply: string, userText = '', prosodyHint = ''): string {
  const r = String(reply || '').toLowerCase();
  const u = String(userText || '').toLowerCase();
  let affect = 'Keep an easy, natural warmth, like chatting with someone you genuinely like.';
  if (/\b(sorry|apolog|unfortunate|afraid|can'?t|cannot|couldn'?t|won'?t|isn'?t|wasn'?t|error|problem|issue|fail(ed|s|ing)?|went wrong|no luck|bad news|not able|unable)\b/.test(r)) {
    affect = 'Sound gently apologetic and understanding — soften the pace, a touch lower and slower, genuinely empathetic, never chirpy.';
  } else if (/\b(congrat|great job|awesome|amazing|perfect|excellent|well done|love it|fantastic|success|all set|good news|nailed it|brilliant|done and)\b/.test(r)) {
    affect = 'Sound bright and genuinely pleased — a warm smile in the voice, a little lift and energy, like sharing good news.';
  } else if (/\b(take your time|no rush|it'?s okay|it'?s alright|don'?t worry|here for you|that sounds hard|i understand|i hear you)\b/.test(r) || /\b(stress|frustrat|angry|upset|worried|anxious|tired|exhausted|overwhelm|struggl)\w*/.test(u)) {
    affect = 'Sound calm, warm and reassuring — unhurried and steady, like you have all the time in the world for them.';
  } else if (r.trim().endsWith('?')) {
    affect = 'Sound lightly curious and inviting — a gentle, open tone that welcomes their answer.';
  }
  // B8: mirror the user's own vibe. `prosodyHint` is derived from HOW they just spoke (energy +
  // pace, measured by the mic VAD — the prosody a text cascade normally throws away), so the
  // reply's delivery leans toward their energy instead of a fixed affect.
  const mirror = prosodyHint ? ' ' + prosodyHint : '';
  return VOICE_MODE_INSTRUCTIONS + ' ' + affect + mirror;
}

export interface SpeechQueue {
  /** Enqueue a ready-to-speak chunk (already cleaned). */
  push(text: string): void;
  /** No more chunks are coming; when the queue drains, onDrained fires. */
  seal(): void;
  /** Barge-in / teardown: stop playback, drop everything pending. */
  cancel(): void;
  /** The text of the chunks that actually STARTED playing — i.e. what the user really heard.
   *  Used for barge-in context truncation so the model's memory matches what was spoken. */
  spokenText(): string;
}

/**
 * FIFO streaming speech player — the heart of the low-latency voice loop. Chunks are
 * synthesised and played IN ORDER, and the NEXT chunk's audio is pre-fetched while the
 * current one plays, so sentences run back-to-back with no gap. The caller pushes sentences
 * as the model streams them, so the FIRST sentence starts speaking within ~1s of it being
 * generated instead of after the whole reply — the difference between "spontaneous" and
 * "laggy". One queue is active per turn; cancel() makes it inert (barge-in).
 */
export function createSpeechQueue(opts: {
  onFirstAudio?: () => void;      // first sound started (thinking → speaking)
  onDrained?: () => void;         // all chunks played, after seal()
  onLimit?: (status: number) => void; // a 429/402 from /api/tts
  shouldPlay?: () => boolean;     // session still live?
  instructions?: string | (() => string | undefined); // delivery steer for /api/tts; a function = per-turn dynamic
}): SpeechQueue {
  const texts: string[] = [];
  const spoken: string[] = []; // chunks that actually began playing → what the user heard (barge truncation)
  let sealed = false, cancelled = false, running = false, firstFired = false;
  let curAudio: HTMLAudioElement | null = null;
  let curDone: (() => void) | null = null;
  let wake: (() => void) | null = null;
  // One controller for the whole turn: barge-in / teardown aborts every in-flight synth fetch
  // (no wasted TTS synth or spend after the user interrupts).
  const abortCtl = new AbortController();
  const signal = () => { const w = wake; wake = null; w?.(); };

  // INLINE transport (/api/tts?inline=1): the server returns the audio BYTES directly, so we
  // skip its disk-write + a second GET — a full round-trip saved per sentence. We wrap the
  // bytes in a blob: URL (revoked after playback, below) instead of hitting the network again.
  const synth = async (text: string): Promise<string | null> => {
    try {
      const token = getToken();
      // Resolve the steer at synth time so a per-turn instruction set AFTER the queue opened
      // (once the reply's affect is known) is still applied to every sentence.
      const instr = typeof opts.instructions === 'function' ? opts.instructions() : opts.instructions;
      const r = await fetch('/api/tts?inline=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text, ...(instr ? { instructions: instr } : {}) }),
        signal: abortCtl.signal,
      });
      if (r.status === 429 || r.status === 402) { cancelled = true; opts.onLimit?.(r.status); signal(); return null; }
      if (!r.ok) return null;
      const buf = await r.arrayBuffer();
      return URL.createObjectURL(new Blob([buf], { type: r.headers.get('Content-Type') || 'audio/mpeg' }));
    } catch {
      return null; // network failure: skip this chunk, keep the conversation going
    }
  };

  const revoke = (u: string | null): void => { if (u && u.startsWith('blob:')) { try { URL.revokeObjectURL(u); } catch { /* noop */ } } };

  const playUrl = (url: string): Promise<void> => new Promise((resolve) => {
    if (cancelled) { revoke(url); resolve(); return; }
    const audio = new Audio(url);
    curAudio = audio; current = audio; liveAudios.add(audio); // shared registry + `current`
    const done = () => {
      liveAudios.delete(audio);
      if (current === audio) current = null;
      if (curAudio === audio) curAudio = null;
      curDone = null;
      revoke(url); // free the blob: URL now that it's played
      resolve();
    };
    curDone = done;
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(done);
  });

  const run = async (): Promise<void> => {
    if (running) return;
    running = true;
    let nextUrl: Promise<string | null> | null = null;
    while (!cancelled) {
      if (texts.length === 0) {
        if (sealed) break;
        await new Promise<void>((r) => { wake = r; }); // sleep until push()/seal()/cancel()
        continue;
      }
      const text = texts.shift() as string;
      const urlP = nextUrl ?? synth(text);
      nextUrl = null;
      const url = await urlP;
      if (cancelled || (opts.shouldPlay && !opts.shouldPlay())) break;
      const peek = texts[0];
      if (peek) nextUrl = synth(peek); // pre-fetch next while this plays
      if (!url) continue;
      if (!firstFired) { firstFired = true; opts.onFirstAudio?.(); }
      spoken.push(text); // this chunk is now being heard — record it for barge truncation
      await playUrl(url);
      if (cancelled) break;
    }
    running = false;
    if (nextUrl) void nextUrl.then(revoke); // free a prefetched-but-unplayed blob URL
    if (!cancelled) opts.onDrained?.();
  };

  return {
    push(text: string) { if (cancelled || sealed) return; const t = text.trim(); if (!t) return; texts.push(t); void run(); signal(); },
    seal() { if (cancelled) return; sealed = true; void run(); signal(); },
    cancel() {
      cancelled = true; texts.length = 0;
      try { abortCtl.abort(); } catch { /* noop */ } // kill any in-flight synth fetch (no wasted spend)
      if (curAudio) { try { curAudio.pause(); } catch { /* noop */ } }
      const d = curDone; curDone = null;
      if (d) d(); // deregister + null current/curAudio + unblock the awaiting run loop
      signal();
    },
    spokenText() { return spoken.join(' '); },
  };
}
